/**
 * useStayWithTutorFlow — la machine de « Garder son tuteur » → les props de `<StayWithTutorFlow>`.
 *
 * Un tuteur arrête un créneau et rien de sa semaine ne tombe dans ceux de la famille : un nouveau
 * tuteur reprendra le créneau, sauf si la famille réserve, avant l'échéance, des créneaux de ce
 * tuteur. Un choix vaut pour une matière : deux heures perdues se gardent en choisissant deux
 * créneaux (`pick`, servi avec les options ; un tuteur souhaité en accepte moins). La ligne
 * « À faire » ouvre cette feuille sur le choix en attente. Quatre appels :
 *  - options : `getTutorSlotMoveOptions` (`GET api/v1/tutor-slot-moves/{id}/options`), à
 *    l'ouverture, et de nouveau après un envoi refusé (la place a pu être prise entre-temps) ;
 *  - réserver : `bookTutorSlotMove` (`POST …/{id}/book`, `{ keys }`) ;
 *  - garder les créneaux avec un nouveau tuteur : `keepTutorSlotMove` (`POST …/{id}/keep`) ;
 *  - garder le tuteur pour l'autre matière de son bloc : `splitTutorSlotMove` (`POST …/{id}/split`).
 * Au succès, le réducteur retire le choix tranché : la ligne « À faire » disparaît ; `onChanged`
 * relit ce que le choix a changé ailleurs (les lives).
 *
 * Rien ne part sans confirmation : un choix complet (le dernier créneau qu'il fallait cocher, ou
 * l'une des deux autres voies) ouvre la question « Confirmer votre choix ? » (`confirming`).
 * « Modifier » la ferme et efface le choix : toucher de nouveau un créneau la repose. Seul un choix de
 * plusieurs créneaux qui peut partir sans être complet (un tuteur souhaité) passe par « Confirmer mes
 * choix ».
 *
 * Les choix sont lus dans `state.data.tutorSlotMoves.moves`, et celui qu'on ouvre est figé : son
 * échéance et son créneau restent ceux que le parent a lus. Un lien de mail peut viser n'importe
 * quel cours du choix (`moveIds`).
 *
 * Les options passent par `useApiFetch`, bornées par `scopeUrlParams` (l'`id` du choix) : une
 * réponse pour un autre choix, ouvert puis fermé, n'est pas la nôtre. L'envoi passe par
 * `useApiMutation`, borné par l'`id` de la route.
 *
 * **Un seul parcours tant qu'un envoi est en vol** (comme `useRecoverAbsenceFlow`) : fermer ne
 * fait que CACHER la feuille, la rouvrir la remontre telle quelle, et la réponse s'écrit dans la
 * feuille si elle est visible, dans le bandeau sinon.
 *
 * Le réducteur est pur et exporté : chaque transition se joue dans un test.
 */
import { useCallback, useEffect, useMemo, useReducer } from "react"
import { useSelector } from "react-redux"

import { tutorSlotMovesService } from "../../../../data/services/tutorSlotMovesService"
import { type SnackbarProps, type SnackbarTone } from "../../../../design-system/molecules/Snackbar"
import { type StayWithTutorFlowProps } from "../../../../design-system/templates/StayWithTutorFlow"
import {
    keepLabelFor,
    toUITutorSlotMoveBookedOutcome,
    toUITutorSlotMoveOptions,
    toUITutorSlotMoves,
    toUITutorSlotMoveSplitOutcome,
    tutorSlotMoveHolds,
    type ApiTutorSlotMove,
    type ApiTutorSlotMoveDecision,
    type ApiTutorSlotMoveOptions,
} from "../../adapters/parent/TutorSlotMoves/tutorSlotMoveAdapter"
import { childDisplayName } from "../formatters/childFormatter"
import {
    type UITutorSlotMove,
    type UITutorSlotMoveChoice,
    type UITutorSlotMoveOption,
    type UITutorSlotMoveOutcome,
    type UITutorSlotMovePick,
    type UITutorSlotMoveSplit,
} from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useApiFetch } from "./useApiFetch"
import { useApiMutation } from "./useApiMutation"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

const OPTIONS_FALLBACK_ERROR = "Impossible de charger les créneaux. Veuillez réessayer."
const SUBMIT_FALLBACK_ERROR = "Impossible d'enregistrer votre choix. Veuillez réessayer."

export type StayWithTutorFlowState = {
    visible: boolean
    /** Le choix en attente, figé à l'ouverture. */
    move: UITutorSlotMove | null
    options: UITutorSlotMoveOption[]
    keepLabel: string
    /** Combien de créneaux choisir — deviné d'après les heures à l'ouverture, servi avec les options. */
    pick: UITutorSlotMovePick
    /** La troisième voie — celle de la ligne à l'ouverture, celle des options ensuite. */
    split: UITutorSlotMoveSplit | null
    /** Vrai, l'étape attend ses options : c'est ce qui fait partir la lecture (effet du crochet). */
    optionsLoading: boolean
    selected: UITutorSlotMoveChoice | null
    /** La question « Confirmer votre choix ? » est ouverte — l'envoi part de là. */
    confirming: boolean
    /** Vrai de l'envoi à sa réponse : fermer ne fait alors que cacher la feuille. */
    submitting: boolean
    error: string | null
    /** Le serveur a dit le choix fermé (échéance passée) : la feuille ne montre plus que son message. */
    closed: boolean
    /** Posé, l'étape est l'accusé ; vide, le choix. */
    outcome: UITutorSlotMoveOutcome | null
    /** Le bandeau de l'accueil — l'issue d'un envoi dont la feuille était cachée. */
    notice: { message: string; tone: SnackbarTone } | null
    /** L'enfant du parcours, pris à l'ouverture. */
    childFirstname: string | null
}

export const initialStayWithTutorFlowState: StayWithTutorFlowState = {
    visible: false,
    move: null,
    options: [],
    keepLabel: "",
    pick: { min: 1, max: 1 },
    split: null,
    optionsLoading: false,
    selected: null,
    confirming: false,
    submitting: false,
    error: null,
    closed: false,
    outcome: null,
    notice: null,
    childFirstname: null,
}

export type StayWithTutorFlowAction =
    | { type: "OPEN"; move: UITutorSlotMove; keepLabel: string; childFirstname?: string | null }
    | {
          type: "OPTIONS_LOADED"
          options: UITutorSlotMoveOption[]
          keepLabel: string
          pick: UITutorSlotMovePick
          split: UITutorSlotMoveSplit | null
      }
    | { type: "OPTIONS_FAILED"; message: string; closed?: boolean }
    /** « Garder avec un nouveau tuteur » ou la troisième voie. */
    | { type: "SELECT"; choice: Exclude<UITutorSlotMoveChoice, { kind: "book" }> }
    /** Un créneau du tuteur coché ou décoché. */
    | { type: "TOGGLE_OPTION"; option: UITutorSlotMoveOption }
    /** « Confirmer mes choix » : poser la question sur un choix de plusieurs créneaux qui peut partir. */
    | { type: "ASK_CONFIRM" }
    /** « Modifier » : la question se ferme et le choix s'efface. */
    | { type: "CANCEL_CONFIRM" }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS"; outcome: UITutorSlotMoveOutcome }
    | { type: "SUBMIT_ERROR"; message: string; closed?: boolean }
    | { type: "CLOSE" }
    | { type: "DISMISS_NOTICE" }

/** Ce que le bandeau dit quand la feuille était cachée à la réponse. */
function noticeFor(outcome: UITutorSlotMoveOutcome): string {
    if (outcome.kind !== "booked") return "Choix enregistré"
    return outcome.count > 1 ? "Créneaux réservés" : "Créneau réservé"
}

/**
 * Le choix peut-il partir ? « Garder avec un nouveau tuteur » et la troisième voie toujours ; des
 * créneaux du tuteur seulement entre `pick.min` et `pick.max`.
 */
export function canConfirmChoice(choice: UITutorSlotMoveChoice | null, pick: UITutorSlotMovePick): boolean {
    if (choice == null) return false
    if (choice.kind !== "book") return true
    return choice.options.length >= pick.min && choice.options.length <= pick.max
}

/**
 * Le choix est-il complet, au point de poser la question sans autre geste ? Les deux autres voies
 * toujours ; des créneaux du tuteur une fois le dernier qu'on peut cocher coché (le serveur borne
 * `pick.max` aux créneaux proposés). Entre `pick.min` et `pick.max`, il peut partir sans être
 * complet : « Confirmer mes choix » pose alors la question.
 */
export function isCompleteChoice(choice: UITutorSlotMoveChoice | null, pick: UITutorSlotMovePick): boolean {
    if (choice == null) return false
    if (choice.kind !== "book") return true
    return choice.options.length >= pick.max
}

/** Des créneaux en cours de relecture (après un refus) ne repartent pas avant d'être revus. */
function canConfirm(state: StayWithTutorFlowState): boolean {
    if (state.optionsLoading && state.selected?.kind === "book") return false
    return canConfirmChoice(state.selected, state.pick)
}

/** Le choix peut-il partir maintenant — ni accusé déjà là, ni envoi en vol ? */
function canSend(state: StayWithTutorFlowState): boolean {
    return state.outcome == null && !state.closed && !state.submitting && canConfirm(state)
}

/**
 * Le choix survit à une nouvelle liste s'il y figure encore : « garder les créneaux » toujours (la
 * carte est là pendant le chargement), la troisième voie si elle est encore offerte, et des
 * créneaux du tuteur ceux qui sont encore proposés — dans la limite de ce qu'on peut en choisir.
 */
function choiceStillOffered(
    choice: UITutorSlotMoveChoice | null,
    options: UITutorSlotMoveOption[],
    pick: UITutorSlotMovePick,
    split: UITutorSlotMoveSplit | null
): UITutorSlotMoveChoice | null {
    if (choice == null || choice.kind === "keep") return choice
    if (choice.kind === "split") return split != null ? choice : null
    const kept = choice.options
        .map((chosen) => options.find((candidate) => candidate.key === chosen.key))
        .filter((option): option is UITutorSlotMoveOption => option != null)
        .slice(0, pick.max)
    return kept.length > 0 ? { kind: "book", options: kept } : null
}

/**
 * Cocher un créneau : avec un seul à choisir, il remplace le précédent ; avec plusieurs, il
 * s'ajoute tant qu'il reste de la place, et un créneau déjà coché se décoche.
 */
function toggledChoice(
    choice: UITutorSlotMoveChoice | null,
    option: UITutorSlotMoveOption,
    pick: UITutorSlotMovePick
): UITutorSlotMoveChoice | null {
    if (pick.max <= 1 || choice?.kind !== "book") return { kind: "book", options: [option] }
    if (choice.options.some((chosen) => chosen.key === option.key)) {
        const options = choice.options.filter((chosen) => chosen.key !== option.key)
        return options.length > 0 ? { kind: "book", options } : null
    }
    if (choice.options.length >= pick.max) return choice
    return { kind: "book", options: [...choice.options, option] }
}

/** Le choix est fermé : plus rien à choisir ni à relire, le message du serveur seul. */
function closedState(state: StayWithTutorFlowState, message: string): StayWithTutorFlowState {
    return {
        ...state,
        closed: true,
        error: message,
        options: [],
        optionsLoading: false,
        selected: null,
        confirming: false,
    }
}

/** Le refus du serveur dit-il le choix fermé ? `code` est rangé dans la tranche avec le message. */
export function isClosedRefusal(error: unknown): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error as any)?.code === "closed" || (error as any)?.response?.data?.code === "closed"
}

export function stayWithTutorFlowReducer(
    state: StayWithTutorFlowState,
    action: StayWithTutorFlowAction
): StayWithTutorFlowState {
    switch (action.type) {
        case "OPEN":
            // Un envoi en vol : c'est SON parcours qu'on remontre, tel quel.
            if (state.submitting) return { ...state, visible: true }
            return {
                ...initialStayWithTutorFlowState,
                notice: state.notice,
                visible: true,
                move: action.move,
                keepLabel: action.keepLabel,
                // Le cas courant, le temps que le serveur le dise : un créneau par heure.
                pick: { min: action.move.hours, max: action.move.hours },
                split: action.move.split,
                optionsLoading: true,
                childFirstname: action.childFirstname ?? null,
            }

        case "OPTIONS_LOADED": {
            if (!state.optionsLoading) return state
            const selected = choiceStillOffered(state.selected, action.options, action.pick, action.split)
            return {
                ...state,
                optionsLoading: false,
                options: action.options,
                keepLabel: action.keepLabel,
                pick: action.pick,
                split: action.split,
                selected,
                // Un choix que la relecture retire ne laisse pas sa question ouverte.
                confirming: state.confirming && selected != null,
            }
        }

        case "OPTIONS_FAILED":
            if (!state.optionsLoading) return state
            if (action.closed) return closedState(state, action.message)
            return { ...state, optionsLoading: false, options: [], error: action.message }

        case "SELECT":
            if (!state.visible || state.outcome != null || state.closed || state.submitting) return state
            if (action.choice.kind === "split" && state.split == null) return state
            return { ...state, selected: action.choice, confirming: true, error: null }

        case "TOGGLE_OPTION": {
            if (!state.visible || state.outcome != null || state.closed || state.submitting) return state
            const selected = toggledChoice(state.selected, action.option, state.pick)
            if (selected === state.selected) return state
            return {
                ...state,
                selected,
                confirming: isCompleteChoice(selected, state.pick),
                error: null,
            }
        }

        case "ASK_CONFIRM":
            if (!canSend(state)) return state
            return { ...state, confirming: true }

        case "CANCEL_CONFIRM":
            // Pendant l'envoi, la question attend sa réponse.
            if (!state.confirming || state.submitting) return state
            return { ...state, confirming: false, selected: null }

        case "SUBMIT":
            if (!canSend(state)) return state
            return { ...state, submitting: true, error: null }

        case "SUBMIT_SUCCESS":
            if (!state.submitting) return state
            if (!state.visible) {
                return {
                    ...initialStayWithTutorFlowState,
                    notice: { message: noticeFor(action.outcome), tone: "success" },
                }
            }
            return { ...state, submitting: false, confirming: false, error: null, outcome: action.outcome }

        case "SUBMIT_ERROR":
            if (!state.submitting) return state
            if (!state.visible) {
                return { ...initialStayWithTutorFlowState, notice: { message: action.message, tone: "danger" } }
            }
            if (action.closed) return closedState({ ...state, submitting: false }, action.message)
            // La place a pu être prise, la fenêtre a pu se fermer : le message reste à l'écran pendant
            // que les options sont relues — il explique pourquoi elles ont changé. Le choix attend
            // la relecture, qui n'en garde que ce qui est encore proposé.
            return {
                ...state,
                submitting: false,
                confirming: false,
                error: action.message,
                options: [],
                optionsLoading: true,
            }

        case "CLOSE":
            // Pendant l'envoi, la feuille se cache seulement : la réponse doit retrouver son parcours.
            if (state.submitting) return { ...state, visible: false }
            return { ...initialStayWithTutorFlowState, notice: state.notice }

        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }

        default:
            return state
    }
}

/** Référence stable : un `?? []` dans un sélecteur rendrait un tableau neuf à chaque lecture. */
const NO_MOVES: ApiTutorSlotMove[] = []

export type StayWithTutorFlowConfig = {
    /** Prénom de l'enfant — l'écran le connaît déjà. */
    childFirstname?: string | null
    /** L'élève affiché : les choix d'un autre enfant ne s'ouvrent pas. */
    studentId?: string | number | null
    /** Rejoué au succès — la ligne « À faire » disparaît d'elle-même (le réducteur retire le choix tranché). */
    onChanged?: () => void
    /** Place à réserver sous le bandeau d'accusé — la hauteur de la barre d'onglets. */
    snackbarBottomOffset?: number
    /**
     * Le choix qu'un lien (le mail de la famille) demande d'ouvrir : la feuille s'ouvre dessus dès
     * qu'il est chargé pour l'élève affiché, puis `onOpenedFromLink` éteint la demande.
     */
    openMoveId?: string | number | null
    onOpenedFromLink?: () => void
}

export type StayWithTutorFlowAdapter = {
    open: (moveId: string | number) => void
    flowProps: StayWithTutorFlowProps
    notice: SnackbarProps
}

/**
 * Un seul nom de l'ouverture aux trois écritures, pour que l'entonnoir se lise ; le choix
 * (réserver, garder, partager) se lit côté serveur, sur le déplacement de créneau.
 */
const STAY_WITH_TUTOR = "stay_with_tutor"

export function useStayWithTutorFlow(config: StayWithTutorFlowConfig = {}): StayWithTutorFlowAdapter {
    const {
        childFirstname,
        studentId = null,
        onChanged,
        snackbarBottomOffset = 0,
        openMoveId = null,
        onOpenedFromLink,
    } = config
    const [state, dispatchUi] = useReducer(stayWithTutorFlowReducer, initialStayWithTutorFlowState)
    // Stable : le bandeau réarme son minuteur de retrait à chaque nouveau `onDismiss`.
    const dismissNotice = useCallback(() => dispatchUi({ type: "DISMISS_NOTICE" }), [])

    const moves = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => (s?.data?.tutorSlotMoves?.moves ?? NO_MOVES) as ApiTutorSlotMove[]
    )
    const student = useSelector(selectDisplayedStudent)

    // ── Options ──────────────────────────────────────────────────────────────────────────────

    const { fetchWith: readOptions, cancel: cancelOptions } = useApiFetch<ApiTutorSlotMoveOptions>({
        service: tutorSlotMovesService.getTutorSlotMoveOptions,
        fetchOnMount: false,
        scopeUrlParams: ["id"],
        idleOnError: true,
        onSuccess: (payload) =>
            dispatchUi({ type: "OPTIONS_LOADED", ...toUITutorSlotMoveOptions(payload, state.move) }),
        onError: (error) =>
            dispatchUi({
                type: "OPTIONS_FAILED",
                message: apiErrorMessage(error, OPTIONS_FALLBACK_ERROR),
                closed: isClosedRefusal(error),
            }),
    })

    // Le réducteur décide quand lire : à l'ouverture et après un envoi refusé. Dès qu'il n'attend
    // plus (réponse lue, fermeture), la lecture en cours est coupée.
    const optionsFor = state.optionsLoading ? state.move : null
    useEffect(() => {
        if (optionsFor == null) return
        readOptions({ id: optionsFor.id })
        return cancelOptions
    }, [optionsFor, readOptions, cancelOptions])

    // ── Envoi ────────────────────────────────────────────────────────────────────────────────

    const succeed = (outcome: UITutorSlotMoveOutcome) => {
        onChanged?.()
        dispatchUi({ type: "SUBMIT_SUCCESS", outcome })
    }
    const fail = (message: string, error: unknown) =>
        dispatchUi({ type: "SUBMIT_ERROR", message, closed: isClosedRefusal(error) })

    const { start: startBook, gesture } = useApiMutation({
        service: tutorSlotMovesService.bookTutorSlotMove,
        gesture: STAY_WITH_TUTOR,
        fallback: SUBMIT_FALLBACK_ERROR,
        scopeParam: "id",
        skipWhilePending: true,
        onSuccess: (data) => {
            const chosen = state.selected?.kind === "book" ? state.selected.options : null
            if (chosen == null) return
            succeed(toUITutorSlotMoveBookedOutcome(data as ApiTutorSlotMoveDecision | null, chosen))
        },
        onError: fail,
    })

    const { start: startKeep } = useApiMutation({
        service: tutorSlotMovesService.keepTutorSlotMove,
        gesture: STAY_WITH_TUTOR,
        fallback: SUBMIT_FALLBACK_ERROR,
        scopeParam: "id",
        skipWhilePending: true,
        onSuccess: () => succeed({ kind: "kept" }),
        onError: fail,
    })

    const { start: startSplit } = useApiMutation({
        service: tutorSlotMovesService.splitTutorSlotMove,
        gesture: STAY_WITH_TUTOR,
        fallback: SUBMIT_FALLBACK_ERROR,
        scopeParam: "id",
        skipWhilePending: true,
        onSuccess: (data) =>
            succeed(toUITutorSlotMoveSplitOutcome(data as ApiTutorSlotMoveDecision | null, state.split)),
        onError: fail,
    })

    // ── Gestes ───────────────────────────────────────────────────────────────────────────────

    // Lus une fois par liste : l'ouverture et le lien du mail cherchent dans les mêmes choix.
    const studentMoves = useMemo(() => toUITutorSlotMoves(moves, studentId), [moves, studentId])
    const open = useCallback(
        (moveId: string | number) => {
            const move = studentMoves.find((candidate) => tutorSlotMoveHolds(candidate, moveId))
            if (move == null) return
            gesture.opened()
            dispatchUi({
                type: "OPEN",
                move,
                keepLabel: keepLabelFor(move),
                childFirstname: childFirstname ?? null,
            })
        },
        [studentMoves, childFirstname, gesture]
    )
    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])

    // `open` ne trouve rien tant que le choix n'est pas chargé pour l'élève affiché : la feuille
    // s'ouvre dès qu'il l'est, et la demande s'éteint aussitôt. Le lien peut viser n'importe quel
    // cours du choix.
    const linkedMoveLoaded =
        openMoveId != null && studentMoves.some((candidate) => tutorSlotMoveHolds(candidate, openMoveId))
    useEffect(() => {
        if (!linkedMoveLoaded || openMoveId == null) return
        open(openMoveId)
        onOpenedFromLink?.()
    }, [linkedMoveLoaded, openMoveId, open, onOpenedFromLink])

    const confirm = useCallback(() => {
        const { move, selected } = state
        if (move == null || selected == null || !canConfirm(state)) return
        let sent: boolean
        if (selected.kind === "book") {
            const keys = selected.options.map((option) => option.key)
            // `key` seul : ce qu'un serveur d'avant les choix par matière sait lire.
            sent = startBook({ id: move.id }, keys.length === 1 ? { keys, key: keys[0] } : { keys })
        } else if (selected.kind === "split") {
            sent = startSplit({ id: move.id })
        } else {
            sent = startKeep({ id: move.id })
        }
        if (sent) dispatchUi({ type: "SUBMIT" })
    }, [state, startBook, startKeep, startSplit])

    return {
        open,
        flowProps: {
            visible: state.visible,
            onClose: close,
            step: state.outcome != null ? "done" : state.closed ? "closed" : "choice",
            childFirstname: childDisplayName(
                state.visible || state.submitting ? state.childFirstname : childFirstname,
                student
            ),
            move: state.move,
            options: state.options,
            keepLabel: state.keepLabel,
            pick: state.pick,
            split: state.split,
            optionsLoading: state.optionsLoading,
            selected: state.selected,
            onSelect: (choice) => dispatchUi({ type: "SELECT", choice }),
            onToggleOption: (option) => dispatchUi({ type: "TOGGLE_OPTION", option }),
            confirming: state.confirming,
            onAskConfirm: () => dispatchUi({ type: "ASK_CONFIRM" }),
            onCancelConfirm: () => dispatchUi({ type: "CANCEL_CONFIRM" }),
            onConfirm: confirm,
            canConfirm: canConfirm(state),
            confirmLoading: state.submitting,
            outcome: state.outcome,
            errorMessage: state.error ?? undefined,
        },
        notice: {
            visible: state.notice != null,
            message: state.notice?.message ?? "",
            tone: state.notice?.tone ?? "success",
            onDismiss: dismissNotice,
            bottomOffset: snackbarBottomOffset,
        },
    }
}
